import React from 'react';
import { Input, UserAvatar, WithLoadingComponent, DropdownTemplate, PaginatorContainer } from "components/shared";
import { Tag } from "primereact/tag"
import { FlapHeader } from 'components/layout';
import { SupervisorService, UsersService } from 'services/http';
import Utils, { AGENT_STATUS, INNER_CONTAINER_HEIGHT } from 'utils';
import utils from 'utils';
import { useHistory } from 'react-router';
import { SocketContext } from 'services/';

/**
 * Agents status main supervision component
 * @returns React.Component
 */
export const SupervisorAgentStatusBis = () => {
    return <>
        <FlapHeader
            primeary
            justify
            className="mx-0"
            leftSide={<span className="pt-1">Agents Supervision</span>}
            rightSide={<aside className="d-flex">
                <AgentStatusFilterTemplate />
                <AgentSearchFilter />
            </aside>}
        />
        <SupervisorAgentsHoc />
    </>
}

/**
 * Higher Order Component for agents supervision
 * @returns React.Component
 */
const SupervisorAgentsHoc = () => {
    const Hoc = React.useMemo(() =>
        WithLoadingComponent(SupervisorAgentWrapper,
            () => SupervisorService.getUsers(),
            Utils.GridLoader())(),
        []);
    return <Hoc />
}

/**
 * Wrapped compoenent for agents supervision
 * @param {Object} props Object to be populated from hoc component
 * @returns React.Component
 */
const SupervisorAgentWrapper = (props) => {
    const { data } = props;
    const [agents, setAgents] = React.useState(data);
    const history = useHistory();
    let filterParam = JSON.parse(new URLSearchParams(history.location.search).get("filter"));
    const socketContext = React.useContext(SocketContext);
    let userSubscription;

    /**
     * Subscribe to the websocket user topic
     */
    React.useEffect(() => {
        async function stompPromise() { return await socketContext.getStompClient(); }
        stompPromise().then((stomp) => {
            userSubscription = stomp.subscribe(`${process.env.REACT_APP_SOCKET_USER_TOPIC}`, callbackRecieve);
        });
        return () => { userSubscription && userSubscription.unsubscribe() }
    }, []);

    const callbackRecieve = (data) => {
        let payload = JSON.parse(data.body).content;
        Promise.resolve(
            setAgents(agents.map(agent => {
                if (agent.username === payload.username) {
                    agent["statusUser"] = payload.statusUser;
                    agent["connected"] = payload.connected;
                    agent["capacityCurrent"] = payload.capacityCurrent;
                }
                return agent;
            }))).then(() => {
                populateFiltredAgents();
            });

    }

    /**
     * This method do the matching filter criterias based on search key and selected status
     * @param {Object} Agent props body 
     * @returns Boolean
     */
    const onSearchTrigger = (item) => {
        let filterParam = JSON.parse(new URLSearchParams(history.location.search).get("filter"));
        let searchKey = filterParam.search ? filterParam.search.toLowerCase() : ``;
        return (item?.firstName?.toLowerCase().includes(searchKey) ||
            item?.lastName?.toLowerCase().includes(searchKey) ||
            item?.username?.toLowerCase().includes(searchKey) ||
            `${item?.firstName} ${item?.lastName}`.toLowerCase().includes(searchKey) ||
            item?.email?.toLowerCase().includes(searchKey))
            && (filterParam.status ? (item.connected ? item?.statusUser?.statusUserId === filterParam.status : (filterParam.status === -1)) : true)
    }

    /**
     * Populate filtred agents on rendred state
     */
    const populateFiltredAgents = () => {
        let filterParam = JSON.parse(new URLSearchParams(history.location.search).get("filter")) || {};
        if (filterParam.search || filterParam.status)
            setAgents(data.filter(item => onSearchTrigger(item)));
        else setAgents(data);
    }

    /**
     * handle the filter change fields
     */
    React.useEffect(() => {
        populateFiltredAgents();
    }, [filterParam?.search, filterParam?.status]);

    /**
     * Agent item template
     * @param {Object} param0 Agent Props
     * @returns <React.Component>
     */
    const AgentCardTemplate = ({ username, firstName, lastName, email, statusUser, capacityNormal, capacityCurrent, capacityMax, connected }) => {
        let agentStatusProps = connected ? AGENT_STATUS.filter(status => status.STATUS_ID === statusUser?.statusUserId)[0] : AGENT_STATUS.filter(item => item.STATUS_ID === -1)[0];
        return <div className='card shadow mt-3 mx-2 mb-1' style={{ height: "fit-content" }}>
            <div className='card-body' style={{ padding: "0" }}>
                <div className='text-center'>
                    <div className='mb-2 pt-3 pb-4' style={{ backgroundColor: agentStatusProps.FLAT_COLOR ? agentStatusProps?.FLAT_COLOR : agentStatusProps?.COLOR, borderRadius: "0.25rem  0.25rem 0 0", color: agentStatusProps.TXT_COLOR ? agentStatusProps.TXT_COLOR : "white" }}>
                        <h6 style={{ marginBottom: "-2px" }}>{`${firstName} ${lastName}`}</h6>
                        <span>{utils.shorthandString(email, 22) || `NaN`}</span>
                    </div>
                    <div className='mt-1' style={{ position: "relative", top: "-30px", left: "0%" }}>
                        <UserAvatar label={`${firstName ? firstName.charAt(0).toUpperCase() : username.charAt(0).toUpperCase()}${lastName ? lastName.charAt(0).toUpperCase() : username.charAt(1).toUpperCase()}`}
                            size="large"
                            style={{ backgroundColor: "#dadbdc" }} />
                    </div>
                </div>
                <div className='text-center mb-3' style={{ marginTop: "-20px" }}>
                    <div>Username {username}</div>
                    <Tag value={connected ? statusUser?.description : agentStatusProps?.LABEL}
                        icon={agentStatusProps?.ICON}
                        className='py-0'
                        style={{ backgroundColor: agentStatusProps.COLOR ? agentStatusProps.COLOR : agentStatusProps.FLAT_COLOR, color: agentStatusProps?.NESTED_COLOR }} />
                </div>
            </div>
            <div className='card-footer'>
                <div className='row'>
                    <div className='col-sm-4 border-right text-center'>
                        <small className='text-center'>Normal</small><br />
                        <span className='badge badge-success'>{capacityNormal}+</span>
                    </div>
                    <div className='col-sm-4 border-right text-center'>
                        <small className='text-center'>Current</small><br />
                        <span className='badge badge-warning'>{capacityCurrent}+</span>
                    </div>
                    <div className='col-sm-4 text-center'>
                        <small className='text-center'>Max</small><br />
                        <span className='badge badge-danger'>{capacityMax}+</span>
                    </div>
                </div>
            </div>
        </div>
    }

    return (
        <>
            {
                agents.length ?
                    <PaginatorContainer
                        defaultRows={20}
                        className='ml-3 pr-2 pb-4 scroll-container'
                        style={{ height: INNER_CONTAINER_HEIGHT, display: "grid", gridTemplateColumns: "25% 25% 25% 25%" }}>
                        {agents.map(agent => { return <AgentCardTemplate {...agent} /> })}
                    </PaginatorContainer>
                    : <div className='mt-5'>{Utils.NoItemsTemplate()}</div>
            }
        </>
    );
}

/**
 * Agents Supervision filter search component
 * @returns React.Component
 */
const AgentSearchFilter = () => {
    let filterParam = JSON.parse(new URLSearchParams(window.location.search).get("filter"));
    const [searchValue, setSearchValue] = React.useState(filterParam?.search);
    const history = useHistory();

    /**
     * Populate filter search key on url search parameter
     */
    React.useEffect(() => {
        utils.pushURLSearchParams(history, "filter", JSON.stringify({ ...filterParam, search: searchValue }));
    }, [searchValue]);

    /**
     * Reset filter search value to empty when reset URL search parameter
     */
    React.useEffect(() => {
        !filterParam && setSearchValue(``);
    }, [filterParam]);

    return <Input placeholder="Search"
        value={searchValue}
        icon="bi bi-search"
        onChange={(e) => setSearchValue(e.target.value)} />
}

/**
 * Agents Supervision filter status component
 * @returns React.Component
 */
const AgentStatusFilterTemplate = () => {
    const filterParam = JSON.parse(new URLSearchParams(window.location.search).get("filter"));
    const [statusOptions, setStatusOptions] = React.useState([{ statusUserId: AGENT_STATUS[1].STATUS_ID, description: AGENT_STATUS[1].LABEL }]);
    const [status, setStatus] = React.useState(filterParam?.status || statusOptions[0].statusUserId);
    const history = useHistory();

    /**
     * Fetch user status options
     */
    React.useEffect(() => {
        UsersService.getAllStatus().then((response) => {
            setStatusOptions([...new Set(Utils.bindAgentStatusOptions(statusOptions.concat(response.result).concat([{ statusUserId: AGENT_STATUS[0].STATUS_ID, description: AGENT_STATUS[0].LABEL }])))]);
        }, error => { alert(JSON.stringify(error)) });
    }, []);

    /**
    * Populte filter status on url search parameter
    */
    React.useEffect(() => {
        utils.pushURLSearchParams(history, "filter", JSON.stringify({ ...filterParam, status }));
    }, [status]);

    /**
     * Reset selected filter status to inital item [ALL_Status] when reset URL search parameter
     */
    React.useEffect(() => {
        (!filterParam?.status && filterParam?.status !== 0) && setStatus(statusOptions[0].id);
    }, [filterParam]);

    return <div className='d-flex'>
        <small className='m-2 text-nowrap'>Status</small>
        <DropdownTemplate
            options={statusOptions}
            selectedItem={status}
            optionLabel="value"
            optionValue="id"
            className="sm-dropdown mr-3"
            scrollHeight={200}
            onItemChange={(e) => { setStatus(e.value) }}
        />
    </div>
}